// src/features/inventory/pages/ItemDetailPage/index.jsx
import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, darken, lighten, Alert, Box, Button, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInventoryItems } from '../../hooks/useInventoryItems';
import { usePermissions, useInventoryItemPermissions, useInventoryStockPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import ItemHeader from './ItemHeader';
import StockStatusCard from './StockStatusCard';
import ItemTabs from './tabs';
import HazardDot, { HAZARD_COLOR, hazardLevelText } from '../../components/HazardDot';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

export default function ItemDetailPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const [, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canUpdate, canDelete } = useInventoryItemPermissions();
  const { canRead: canReadStock, canRecord: canRecordStock } = useInventoryStockPermissions();
  const { hasScope } = usePermissions();

  const {
    currentItem,
    loading,
    error,
    clearError,
    fetchItemById,
    updateItem,
    deleteItem,
  } = useInventoryItems();

  useEffect(() => {
    fetchItemById(itemId);
  }, [fetchItemById, itemId]);

  const handleToggleActive = useCallback(async () => {
    if (!canUpdate || !currentItem) return;
    const result = await updateItem(itemId, { active: !currentItem.active });
    if (!result.success) showError(result.error);
  }, [canUpdate, currentItem, itemId, updateItem, showError]);

  const handleDelete = useCallback(() => {
    showConfirmDialog({
      title: t('inventory:items.confirm.delete_title'),
      message: t('inventory:items.confirm.delete_message', { name: currentItem?.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteItem(itemId);
        if (result.success) {
          showSuccess(t('inventory:items.success.deleted'));
          navigateBack('..', { replace: true });
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteItem, itemId, currentItem, showSuccess, showError, navigateBack, t]);

  if (loading && !currentItem) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!currentItem) return null;

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <ItemHeader
        currentItem={currentItem}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onToggleActive={handleToggleActive}
        onEdit={() => navigate('edit')}
        onDelete={handleDelete}
        onBack={() => navigateBack('..')}
        onTabNavigate={(tab) => setSearchParams({ tab })}
      />

      {/* Hazardous items warn before anything else, with the sheet one click away */}
      {currentItem.product_group?.hazardous && currentItem.hazard_level && (
        <Alert
          severity="warning"
          data-testid="item-hazard-warning"
          icon={<HazardDot level={currentItem.hazard_level} size={14} tooltip={false} sx={{ alignSelf: 'center' }} />}
          action={hasScope('inventory:msds:read') && (
            <Button color="inherit" size="small" onClick={() => setSearchParams({ tab: 'hazardous' })}>
              {t('inventory:msds.hazard_level.open_sheet')}
            </Button>
          )}
          // Tinted in the level's own colour: the theme's warning is orange and
          // would make every level read as red
          sx={(theme) => {
            const c = HAZARD_COLOR[currentItem.hazard_level];
            const dark = theme.palette.mode === 'dark';
            return {
              mb: 3,
              alignItems: 'center',
              bgcolor: alpha(c, dark ? 0.16 : 0.14),
              color: dark ? lighten(c, 0.5) : darken(c, 0.55),
              border: 1,
              borderColor: alpha(c, 0.5),
            };
          }}
        >
          <strong>{t('inventory:msds.hazard_level.label')}: {hazardLevelText(t, currentItem.hazard_level)}.</strong>
          {' '}{t('inventory:msds.hazard_level.warning')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {currentItem.product_group?.inventory_tracked && canReadStock && (
        <StockStatusCard
          item={currentItem}
          itemId={itemId}
          canUpdate={canRecordStock}
          onTransactionRecorded={() => fetchItemById(itemId)}
        />
      )}

      <ItemTabs
        itemId={itemId}
        item={currentItem}
        onStockChanged={() => fetchItemById(itemId)}
        onMsdsChanged={() => fetchItemById(itemId)}
      />

      <ConfirmDialog />
    </Box>
  );
}
